import {
  Button,
  Disclaimer,
  H5,
  Pill,
  TextWithTooltipIcon,
  TooltipWrapper,
  TruncatedText,
  displayCentsInDollars,
  lightTheme,
} from '@vgrubs/components';
import {
  ClientPaymentEntity,
  ClientPaymentStatus,
} from 'components/src/types/clientPayments.types';
import { DateTime } from 'luxon';
import { Link } from 'react-router-dom';

/**
 * Widths shared with the list's TableHeader, so the columns line up. The
 * Retry column only exists while some payment on screen can be retried;
 * otherwise its share goes to the other columns instead of sitting empty.
 */
export const clientPaymentsColumns = (withRetry: boolean) =>
  withRetry
    ? [
        { label: 'Transaction ID', width: '9%' },
        { label: 'Date', width: '11%' },
        { label: 'Owner', width: '18%' },
        { label: 'Location', width: '21%' },
        { label: 'Status', width: '12%' },
        { label: 'Amount', width: '12%' },
        { label: '', width: '17%' },
      ]
    : [
        { label: 'Transaction ID', width: '11%' },
        { label: 'Date', width: '14%' },
        { label: 'Owner', width: '22%' },
        { label: 'Location', width: '26%' },
        { label: 'Status', width: '14%' },
        { label: 'Amount', width: '13%' },
      ];

const STATUS_LOOK: Record<
  ClientPaymentStatus,
  { name: string; color: string }
> = {
  [ClientPaymentStatus.initiated]: {
    name: 'Initiated',
    color: lightTheme.darkGray,
  },
  [ClientPaymentStatus.completed]: {
    name: 'Completed',
    color: lightTheme.green,
  },
  [ClientPaymentStatus.failed]: { name: 'Failed', color: lightTheme.primary },
  [ClientPaymentStatus.chargedBack]: {
    name: 'Charged Back',
    color: lightTheme.primary,
  },
  [ClientPaymentStatus.cancelled]: {
    name: 'Cancelled',
    color: lightTheme.primary,
  },
};

/** Only these two can be charged again. */
export const canRetryPayment = (payment: ClientPaymentEntity) =>
  [ClientPaymentStatus.failed, ClientPaymentStatus.chargedBack].includes(
    payment.status
  );

export const formatPaymentDate = (payment: ClientPaymentEntity) =>
  DateTime.fromISO(payment.createdAt).toFormat('MMM dd, yyyy');

export const PaymentStatusPill = ({
  status,
}: {
  status: ClientPaymentStatus;
}) => {
  const look = STATUS_LOOK[status];
  return (
    <Pill $background={look?.color ?? lightTheme.darkGray}>
      <Disclaimer $m="0" $color={lightTheme.white} $semiBold $noWrap>
        {look?.name ?? status}
      </Disclaimer>
    </Pill>
  );
};

/** The amount charged, coloured by status, with the fee split in its tooltip. */
export const PaymentAmount = ({
  payment,
}: {
  payment: ClientPaymentEntity;
}) => (
  <TextWithTooltipIcon
    bold
    textColor={STATUS_LOOK[payment.status]?.color}
    text={displayCentsInDollars(payment.amount)}
    tooltip={`
      Net Amount: ${displayCentsInDollars(payment.amountWithoutFees)} <br/>
      Transaction Fee: ${displayCentsInDollars(payment.fee)} <br/>
      ${
        payment.cancellationFee
          ? `Failure Fee: <span style="color: ${
              lightTheme.primary
            }">${displayCentsInDollars(payment.cancellationFee)}</span>`
          : ''
      }
    `}
  />
);

/**
 * TooltipWrapper is inline-block, and its "normal" line-height builds a line
 * box taller than the text, which left these cells 2px lower than the plain
 * ID and date beside them. As a flex box it is exactly as tall as the text.
 */
const LINK_CELL_STYLE = { display: 'flex', minWidth: 0 } as const;

/** Lets a long name shrink to its ellipsis instead of spilling over. */
const LINK_STYLE = { minWidth: 0 };

const ownerName = (payment: ClientPaymentEntity) =>
  `${payment.owner.firstName} ${payment.owner.lastName}`;

/** `title` sets it at heading size, for the top of a phone card. */
export const OwnerLink = ({
  payment,
  title,
}: {
  payment: ClientPaymentEntity;
  title?: boolean;
}) => {
  const Text = title ? H5 : Disclaimer;
  return (
    <TooltipWrapper
      tooltip={ownerName(payment)}
      $maxWidth="100%"
      style={LINK_CELL_STYLE}
    >
      <Link to={`/users/${payment.owner.id}`} style={LINK_STYLE}>
        <Text $m="0">
          <TruncatedText underline>{ownerName(payment)}</TruncatedText>
        </Text>
      </Link>
    </TooltipWrapper>
  );
};

export const LocationLink = ({ payment }: { payment: ClientPaymentEntity }) => (
  <TooltipWrapper
    tooltip={payment.location.name}
    $maxWidth="100%"
    style={LINK_CELL_STYLE}
  >
    <Link to={`/locations/${payment.location.id}`} style={LINK_STYLE}>
      <Disclaimer $m="0">
        <TruncatedText underline>{payment.location.name}</TruncatedText>
      </Disclaimer>
    </Link>
  </TooltipWrapper>
);

export const RetryPaymentButton = ({ onClick }: { onClick: () => void }) => (
  <Button $selected $small $w="100%" $wSM="100%" onClick={onClick}>
    Retry Payment
  </Button>
);
